import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconPause } from "blocks-nextjs/blocks-react/components/Icons/Pause";
import { CatIconPlayArrow } from "blocks-nextjs/blocks-react/components/Icons/PlayArrow";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { throttle } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import TimelineVerticalLine from "../../assets/TimelineVerticalLine.svg";
import APIError from "../../components/APIError/APIError";
import { getBatteryIcon } from "../../lib/getBatteryIcon";
import { getAssetImageIcon, trimAssetName } from "../../pages/Projects/ProjectDetails.helper";
import { isEmpty } from "../../shared/helper";
import useAppConfigStore from "../../store/useAppConfigStore";
import useGlobalStore from "../../store/useGlobalStore";
import NoDataMessage from "../DataError/NoDataMessage";
import Timeline from "./Timeline";
const ActivityTimeline = ({ assetChargeActivityData, assetDetailsApiStatus, assetsDetails, metricsDate, refetchAssetDetails, replayTimeline, setReplayTimelineValue }) => {
    const isLoading = useGlobalStore((state) => state.isLoading);
    const TIME_CONSTANTS = {
        MAX_VALID_HOURS: 23,
        MAX_VALID_MINUTES: 59,
        MINUTES_PER_HOUR: 60,
        SECONDS_IN_A_DAY: 86400,
        SECONDS_IN_TEN_MINUTES: 600,
        SECONDS_PER_HOUR: 3600,
        SLIDER_UPDATE_INTERVAL: 250,
        TOTAL_HRS: "23:59:59"
    };
    const [rangeValue, setRangeValue] = useState(0);
    const [sliderPostion, setSliderPostion] = useState(0);
    const [sliderValue, setSliderValue] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [gotoTime, setGotoTime] = useState("");
    const socRefsMap = useRef(new Map());
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const showOpacity = (appConfig?.release_1_1?.showReplaySlider && replayTimeline > 0) || !appConfig?.release_1_1?.showReplaySlider;
    const assetChargeActivityDataFormatted = useMemo(() => {
        return assetChargeActivityData.map((item) => {
            const { activityEndTimeStamp = "", activityStartTimeStamp = "" } = item;
            const todayDate = new Date().toISOString().split("T")[0] + "T";
            if (activityStartTimeStamp && activityStartTimeStamp.indexOf("T") === -1) {
                item.activityStartTimeStamp = todayDate + activityStartTimeStamp;
            }
            if (activityEndTimeStamp && activityEndTimeStamp.indexOf("T") === -1) {
                item.activityEndTimeStamp = todayDate + activityEndTimeStamp;
            }
            if (activityStartTimeStamp.indexOf(".000Z") !== -1 || activityEndTimeStamp.indexOf(".000Z") !== -1) {
                item.activityStartTimeStamp = activityStartTimeStamp.replace(/\.000Z$/, "");
                item.activityEndTimeStamp = activityEndTimeStamp.replace(/\.000Z$/, "");
            }
            return item;
        });
    }, [assetChargeActivityData]);
    // Use ref to store the original SOC values to avoid stale closures
    const originalSocValuesRef = useRef(new Map());
    // function to update SOC without re-rendering
    const handleSetAssetHoverSoc = (value) => {
        const { hoverChartId, hoverSoc } = value;
        // Update the specific SOC element directly via ref
        if (hoverChartId && socRefsMap.current.has(hoverChartId)) {
            const socElement = socRefsMap.current.get(hoverChartId);
            if (socElement && hoverSoc) {
                socElement.textContent = `${Math.round(Number(hoverSoc))}%`;
            }
        }
        // Reset all SOC values when hover ends (empty value)
        if (!hoverChartId && !hoverSoc) {
            socRefsMap.current.forEach((element, chartId) => {
                const originalSoc = originalSocValuesRef.current.get(chartId);
                if (element) {
                    element.textContent = originalSoc ? `${Math.round(originalSoc)}%` : "0%";
                }
            });
        }
    };
    useEffect(() => {
        //Wrapping this in setTimeout without any delay helps us finding the shadow-root element
        setTimeout(() => {
            //Finding the shadow-root element to modify the styles of the table row asset-portfolio-table-row
            const allRows = document.querySelectorAll(".activity-timeline-row");
            //Commenting this allHeaderCell to use later
            // const allHeaderCell = document.querySelectorAll(".timeline-header-cell");
            allRows.forEach((rowEle) => {
                const tableRowElement = rowEle?.shadowRoot.querySelector(".cat-c-table-row");
                tableRowElement?.setAttribute("style", "border-bottom: 1px solid #CCC");
            });
        });
    });
    useEffect(() => {
        if (appConfig?.release_1_1?.showReplaySlider && replayTimeline && !isPlaying) {
            updateRangeValue(0);
            handlePlayClick();
        }
        if (!replayTimeline) {
            setIsPlaying(false);
        }
    }, [replayTimeline]);
    useEffect(() => {
        setRangeValue(0);
        setSliderValue(0);
        setSliderPostion(0);
    }, [metricsDate, assetsDetails]);
    const throttledSetRangeValue = throttle((newValue, newSliderPostion) => {
        setRangeValue(newValue);
        setSliderValue(newValue);
        setSliderPostion(newSliderPostion);
    }, 100);
    const timeToPixels = (time, containerWidth) => {
        const [hours, minutes] = time.split(":").map(Number);
        const totalMinutes = hours * 60 + minutes;
        const MINUTES_PER_DAY = 1440;
        const pixelsPerMinute = containerWidth / MINUTES_PER_DAY;
        return totalMinutes * pixelsPerMinute;
    };
    const updateRangeValue = (value) => {
        setReplayTimelineValue(value);
        const time = formatTime(value);
        const timeSliderElement = document.getElementById("timeSlider");
        const containerWidth = timeSliderElement ? timeSliderElement.offsetWidth : 0;
        const timeToPixelValue = timeToPixels(time, containerWidth);
        const sliderRightPostion = containerWidth - timeToPixelValue;
        const elements = document.querySelectorAll(".chart-row");
        elements.forEach((element) => {
            // Decreasing the opacity to 0.7 make the chart grey out for the slider out area
            element.style.cssText += `
                position: absolute;
                left: ${timeToPixelValue.toFixed(2)}px;
                top: 0;
                right: 0;
                bottom: 0;
                background: rgba(255,255,255,0.7);
            `;
        });
        throttledSetRangeValue(parseInt(value), sliderRightPostion);
    };
    useEffect(() => {
        let interval;
        if (isPlaying) {
            interval = setInterval(() => {
                setSliderValue((prevValue) => {
                    if (prevValue >= TIME_CONSTANTS.SECONDS_IN_A_DAY) {
                        // 86400 seconds in 24 hours
                        clearInterval(interval);
                        setIsPlaying(false);
                        return TIME_CONSTANTS.SECONDS_IN_A_DAY;
                    }
                    updateRangeValue(prevValue + TIME_CONSTANTS.SECONDS_IN_TEN_MINUTES); // Increase the increment value to make it run faster
                    return prevValue + TIME_CONSTANTS.SECONDS_IN_TEN_MINUTES;
                });
            }, TIME_CONSTANTS.SLIDER_UPDATE_INTERVAL);
            return () => clearInterval(interval);
        }
        else {
            clearInterval(interval);
        }
    }, [isPlaying]);
    const handlePlayClick = () => {
        if (sliderValue >= TIME_CONSTANTS.SECONDS_IN_A_DAY) {
            setSliderValue(0);
            updateRangeValue(0);
        }
        setIsPlaying(true);
    };
    const handlePauseClick = () => {
        setIsPlaying(false);
    };
    const handleGotoChange = (e) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 4) {
            value = value.slice(0, 4);
        }
        if (value?.length >= 1 && value[0] > 2)
            return;
        if (value?.length >= 2 && parseInt(value.slice(0, 2)) > 23)
            return;
        if (value?.length >= 3 && value[2] > 5)
            return;
        if (value?.length >= 4 && parseInt(value.slice(2, 4)) > 59)
            return;
        let formatted = value;
        if (value.length > 2) {
            formatted = `${value.slice(0, 2)}:${value.slice(2)}`;
        }
        else if (value.length === 2) {
            formatted = `${value.slice(0, 2)}`;
        }
        setGotoTime(formatted);
    };
    const validateTime = (value) => {
        // Allow only numbers and ":"
        if (!/^\d{0,2}:?\d{0,2}$/.test(value))
            return false;
        // If input has ":", validate full format
        if (value.includes(":")) {
            const [hh, mm] = value.split(":");
            if (hh.length > 2 || mm.length > 2)
                return false;
            if (hh && (isNaN(hh) || hh < 0 || hh > TIME_CONSTANTS.MAX_VALID_HOURS))
                return false;
            if (mm && (isNaN(mm) || mm < 0 || mm > TIME_CONSTANTS.MAX_VALID_MINUTES))
                return false;
        }
        return true;
    };
    const handleGotoClick = () => {
        if (validateTime(gotoTime)) {
            const [hours, minutes] = gotoTime.split(":").map(Number);
            const timeInSeconds = hours * TIME_CONSTANTS.SECONDS_PER_HOUR + minutes * TIME_CONSTANTS.MINUTES_PER_HOUR;
            setSliderValue(timeInSeconds);
            updateRangeValue(timeInSeconds);
        }
    };
    const populateTimeLineDetails = (assetsDetails) => {
        const filteredAssetsDetails = assetsDetails?.filter((asset) => asset?.assetSourceType.toUpperCase() !== "CHARGING_STATION");
        const showMecCharger = appConfig?.release_1_1?.showMecCharger;
        // Sort a copy of activities by activityStartTimeStamp
        const sortActivities = (activities) => [...activities].sort((a, b) => {
            const timestampA = appConfig?.release_1_1?.showCestTimezone && !a.activityStartTimeStamp.endsWith("Z")
                ? `${a.activityStartTimeStamp}Z`
                : a.activityStartTimeStamp;
            const timestampB = appConfig?.release_1_1?.showCestTimezone && !b.activityStartTimeStamp.endsWith("Z")
                ? `${b.activityStartTimeStamp}Z`
                : b.activityStartTimeStamp;
            return new Date(timestampA).getTime() - new Date(timestampB).getTime();
        });
        // Render a single timeline row for a given activity set + chart ID
        const renderRow = (assetInfo, activity, chartId, connectorId) => {
            const bobMessage = assetInfo?.assetMetadata?.make === "BAB" || assetInfo?.assetMetadata?.make === "PSW"
                ? assetInfo?.assetMetadata?.productFamily?.name
                : assetInfo?.assetMetadata?.make;
            const { chargeEnergySum = 0, chargeEnergySumTime = "", deltaSOC = 0, dischargeEnergySum = 0, dischargeEnergySumTime = "" } = getChargeDetails(activity);
            // Store the original SOC value in the ref
            if (deltaSOC) {
                originalSocValuesRef.current.set(chartId, deltaSOC);
            }
            return (_jsxs("tr", { className: "border-b", children: [_jsx("td", { children: _jsxs("div", { className: "inline-flex", children: [_jsx("div", { children: _jsx("img", { alt: "Assets", className: "w-16 p-3", src: getAssetImageIcon(assetInfo) }) }), _jsxs("div", { className: "cursor-pointer", children: [_jsxs(CatHeading, { "data-test": `${assetInfo?.assetSerialNumber}-assetname`, variant: "display-sm", children: [trimAssetName(assetInfo?.assetName, assetInfo?.assetMetadata), showMecCharger && connectorId !== undefined ? ` - Connector ${connectorId}` : ""] }), _jsxs("span", { "data-test": `${assetInfo?.assetSerialNumber}-assetDetails`, children: [assetInfo?.assetSerialNumber && assetInfo?.assetMetadata?.make
                                                    ? `${assetInfo?.assetSerialNumber} - ${bobMessage}`
                                                    : `${assetInfo?.assetSerialNumber || ""}${bobMessage || ""}`, " ", assetInfo?.assetMetadata?.model] })] })] }) }), _jsx("td", { className: "min-w-[80px]", children: (!showMecCharger || assetInfo?.assetSourceType?.toUpperCase() !== "CHARGER") && (_jsxs("div", { className: "flex items-center", children: [_jsxs("div", { className: "pr-2 min-w-[45px]", "data-test": `${assetInfo?.assetSerialNumber}-socValue`, ref: (el) => {
                                        if (el) {
                                            socRefsMap.current.set(chartId, el);
                                        }
                                    }, children: [deltaSOC ? Math.round(deltaSOC) : 0, "%"] }), " ", getBatteryIcon(Number(deltaSOC))] })) }), _jsx("td", { colSpan: 6, children: _jsx(Timeline, { chartData: activity, chartId: chartId, setAssetHoverSoc: handleSetAssetHoverSoc, showCestTimezone: appConfig?.release_1_1?.showCestTimezone, showOpacity: showOpacity }) }), _jsxs("td", { className: "pl-4 pr-2 text-sm", children: [_jsxs("div", { className: "text-[#1AC69E]", children: [Math.round(chargeEnergySum), " kWh / ", chargeEnergySumTime, " Hr"] }), _jsxs("div", { className: "text-[#FB5A00]", children: [Math.round(dischargeEnergySum), " kWh / ", dischargeEnergySumTime, " Hr"] })] })] }, chartId));
        };
        // Render a single row for a CHARGER asset with all connectors stacked vertically
        const renderChargerRow = (assetInfo, connectorIds, baseActivity) => {
            const bobMessage = assetInfo?.assetMetadata?.make === "BAB" || assetInfo?.assetMetadata?.make === "PSW"
                ? assetInfo?.assetMetadata?.productFamily?.name
                : assetInfo?.assetMetadata?.make;
            return (_jsxs("tr", { className: "border-b", children: [_jsx("td", { children: _jsxs("div", { className: "inline-flex", children: [_jsx("div", { children: _jsx("img", { alt: "Assets", className: "w-16 p-3", src: getAssetImageIcon(assetInfo) }) }), _jsxs("div", { className: "cursor-pointer", children: [_jsx(CatHeading, { "data-test": `${assetInfo?.assetSerialNumber}-assetname`, variant: "display-sm", children: trimAssetName(assetInfo?.assetName, assetInfo?.assetMetadata) }), _jsxs("span", { "data-test": `${assetInfo?.assetSerialNumber}-assetDetails`, children: [assetInfo?.assetSerialNumber && assetInfo?.assetMetadata?.make
                                                    ? `${assetInfo?.assetSerialNumber} - ${bobMessage}`
                                                    : `${assetInfo?.assetSerialNumber || ""}${bobMessage || ""}`, " ", assetInfo?.assetMetadata?.model] })] })] }) }), _jsx("td", { className: "min-w-[80px]" }), _jsx("td", { colSpan: 6, children: _jsx("div", { className: "flex flex-col gap-1", children: connectorIds.map((connectorId) => {
                                const connectorActivity = sortActivities(baseActivity.filter((a) => a.connectorId === connectorId));
                                const chartId = `timeline-chart-id-${assetInfo.assetId}-connector-${connectorId}`;
                                if (connectorActivity.length === 0) {
                                    return (_jsx("div", { className: "relative h-[28px]", children: _jsxs("span", { className: "absolute left-1 top-1/2 -translate-y-1/2 text-xs font-medium px-1", children: ["Connector ", connectorId] }) }, chartId));
                                }
                                return (_jsxs("div", { className: "relative", children: [_jsx(Timeline, { chartData: connectorActivity, chartHeight: 28, chartId: chartId, setAssetHoverSoc: handleSetAssetHoverSoc, showCestTimezone: appConfig?.release_1_1?.showCestTimezone, showOpacity: showOpacity }), _jsxs("span", { className: "absolute left-1 top-1/2 -translate-y-1/2 text-xs font-medium pointer-events-none z-10 px-1 rounded", children: ["Connector ", connectorId] })] }, chartId));
                            }) }) }), _jsx("td", { className: "pl-4 pr-2 text-sm", children: (() => {
                            const allConnectorActivity = sortActivities(baseActivity.filter((a) => a.connectorId !== undefined && a.connectorId !== null));
                            const { chargeEnergySum = 0, chargeEnergySumTime = "0", dischargeEnergySum = 0, dischargeEnergySumTime = "0" } = allConnectorActivity.length > 0 ? getChargeDetails(allConnectorActivity) : {};
                            return (_jsxs("div", { children: [_jsxs("div", { className: "text-[#1AC69E]", children: [Math.round(chargeEnergySum), " kWh / ", chargeEnergySumTime, " Hr"] }), _jsxs("div", { className: "text-[#FB5A00]", children: [Math.round(dischargeEnergySum), " kWh / ", dischargeEnergySumTime, " Hr"] })] }));
                        })() })] }, assetInfo.assetId));
        };
        return filteredAssetsDetails?.flatMap((assetInfo, index) => {
            // Expect full timestamp in ISO format from API when showCestTimezone flag is true
            const baseActivity = (appConfig?.release_1_1?.showCestTimezone
                ? assetChargeActivityData.filter((activityData) => activityData.assetId === assetInfo.assetId)
                : assetChargeActivityDataFormatted.filter((activityData) => activityData.assetId === assetInfo.assetId)).map((item) => item.assetSourceType?.toUpperCase() === "CHARGER"
                ? { ...item, endSoc: item.endSoc ?? 100, startSoc: item.startSoc ?? 100 }
                : item);
            // CHARGER assets: one row with stacked connector timelines (only when showMecCharger is enabled)
            if (showMecCharger && assetInfo?.assetSourceType?.toUpperCase() === "CHARGER") {
                const presentConnectorIds = [
                    ...new Set(baseActivity
                        .filter((a) => a.connectorId !== undefined && a.connectorId !== null)
                        .map((a) => a.connectorId))
                ];
                if (presentConnectorIds.length > 0) {
                    const maxConnectorId = Math.max(...presentConnectorIds);
                    // Fill in all connectors from 1 to max so gaps (e.g. connector 1 missing) show an empty row
                    const connectorIds = Array.from({ length: maxConnectorId }, (_, i) => i + 1);
                    return [renderChargerRow(assetInfo, connectorIds, baseActivity)];
                }
                // No connector data at all — still show the CHARGER row with label-only placeholders
                return [renderChargerRow(assetInfo, [1, 2], baseActivity)];
            }
            // Default: one row per asset
            const sortedActivity = sortActivities(baseActivity);
            const chartId = `timeline-chart-id-${index}`;
            return [renderRow(assetInfo, sortedActivity, chartId)];
        });
    };
    const padZero = (value) => {
        if (value < 10) {
            return "0" + value;
        }
        else {
            return value;
        }
    };
    const getChargeDetails = (chargeActivity) => {
        let currentRangeDate;
        let chargeEnergySum = 0, chargeEnergySumTime = "0:0", chargeEnergyTimeDiffInSec = 0, dischargeEnergySum = 0, dischargeEnergySumTime = "0:0", dischargeEnergyTimeDiffInSec = 0;
        let deltaSOC;
        let lastActivityEndSoc;
        if (chargeActivity.length > 0) {
            chargeActivity.map((item) => {
                const { activity = "", activityEndTimeStamp = "", activityStartTimeStamp = "", chargeEnergy = 0, dischargeEnergy = 0, endSoc = 0, startSoc = 0 } = item;
                // When showCestTimezone is TRUE: API sends timestamps in project's local timezone
                // Append 'Z' to treat as UTC for consistent millisecond values across all users
                const startTimestamp = appConfig?.release_1_1?.showCestTimezone && !activityStartTimeStamp.endsWith("Z")
                    ? `${activityStartTimeStamp}Z`
                    : activityStartTimeStamp;
                const endTimestamp = appConfig?.release_1_1?.showCestTimezone && !activityEndTimeStamp.endsWith("Z")
                    ? `${activityEndTimeStamp}Z`
                    : activityEndTimeStamp;
                const activityStartDate = new Date(startTimestamp);
                const activityEndDate = new Date(endTimestamp);
                if (!currentRangeDate && activityStartTimeStamp) {
                    let currentChartTime = TIME_CONSTANTS.TOTAL_HRS;
                    if (replayTimeline) {
                        currentChartTime =
                            sliderValue > TIME_CONSTANTS.SECONDS_IN_A_DAY - 5 || sliderValue === 0
                                ? TIME_CONSTANTS.TOTAL_HRS
                                : formatTime(rangeValue);
                    }
                    const rangeTimestamp = activityStartTimeStamp.split("T")[0] + "T" + currentChartTime;
                    // When showCestTimezone is TRUE: Append 'Z' to treat as UTC for consistent comparisons
                    const rangeTimestampUTC = appConfig?.release_1_1?.showCestTimezone && !rangeTimestamp.endsWith("Z")
                        ? `${rangeTimestamp}Z`
                        : rangeTimestamp;
                    currentRangeDate = new Date(rangeTimestampUTC);
                }
                if (activityStartDate <= currentRangeDate) {
                    chargeEnergySum += chargeEnergy;
                    if (activity !== "Trickle") {
                        dischargeEnergySum += dischargeEnergy;
                    }
                    if (activity === "Charging" || activity === "AC_Charging" || activity === "DC_Charging") {
                        chargeEnergyTimeDiffInSec += (activityEndDate.getTime() - activityStartDate.getTime()) / 1000;
                    }
                    else {
                        dischargeEnergyTimeDiffInSec +=
                            (activityEndDate.getTime() - activityStartDate.getTime()) / 1000;
                    }
                    // Track the endSoc of the last completed activity
                    if (activityEndDate <= currentRangeDate) {
                        lastActivityEndSoc = endSoc || startSoc;
                    }
                }
                if (currentRangeDate >= activityStartDate && currentRangeDate <= activityEndDate) {
                    deltaSOC = endSoc;
                }
            });
            // If no activity is currently in progress, use the endSoc of the last completed activity
            if (!deltaSOC && lastActivityEndSoc !== undefined) {
                deltaSOC = lastActivityEndSoc;
            }
        }
        // Calculate charge time in hours and minutes
        if (chargeEnergyTimeDiffInSec > 0) {
            const hours = Math.floor(chargeEnergyTimeDiffInSec / TIME_CONSTANTS.SECONDS_PER_HOUR);
            const minutes = Math.floor((chargeEnergyTimeDiffInSec % TIME_CONSTANTS.SECONDS_PER_HOUR) / TIME_CONSTANTS.MINUTES_PER_HOUR);
            chargeEnergySumTime = `${padZero(hours)}:${padZero(minutes)}`;
        }
        // Calculate discharge time in hours and minutes
        if (dischargeEnergyTimeDiffInSec > 0) {
            const hours = Math.floor(dischargeEnergyTimeDiffInSec / TIME_CONSTANTS.SECONDS_PER_HOUR);
            const minutes = Math.floor((dischargeEnergyTimeDiffInSec % TIME_CONSTANTS.SECONDS_PER_HOUR) / TIME_CONSTANTS.MINUTES_PER_HOUR);
            dischargeEnergySumTime = `${padZero(hours)}:${padZero(minutes)}`;
        }
        return {
            chargeEnergySum,
            chargeEnergySumTime,
            deltaSOC,
            dischargeEnergySum,
            dischargeEnergySumTime
        };
    };
    if (assetDetailsApiStatus === "error") {
        return (_jsx("div", { className: "w-full h-[43vh]", children: _jsx(APIError, { refreshHandler: refetchAssetDetails }) }));
    }
    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / TIME_CONSTANTS.SECONDS_PER_HOUR);
        const minutes = Math.floor((seconds % TIME_CONSTANTS.SECONDS_PER_HOUR) / TIME_CONSTANTS.MINUTES_PER_HOUR);
        const formattedTime = String(hours).padStart(2, "0") + ":" + String(minutes).padStart(2, "0");
        return formattedTime === "24:00" ? TIME_CONSTANTS.TOTAL_HRS : formattedTime;
    };
    const timelineStatusMessage = () => {
        if (isLoading) {
            return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh]", "data-test": "loading", children: _jsx(CatLoadingIndicator, {}) }));
        }
        return (_jsx("div", { className: "w-full h-[46vh]", children: _jsx(NoDataMessage, { date: metricsDate }) }));
    };
    return (_jsx("div", { className: "relative", children: _jsx(_Fragment, { children: isLoading || (assetDetailsApiStatus === "success" && assetsDetails?.length === 0) ? (_jsx(_Fragment, { children: timelineStatusMessage() })) : (_jsx(_Fragment, { children: !isEmpty(assetsDetails) && (_jsxs("div", { className: "overflow-y-auto h-[470px]", "data-test": "timeline-data", id: "assets-portfoilo-table", children: [appConfig?.release_1_1?.showReplaySlider && replayTimeline > 0 && (_jsxs("div", { className: "flex border-b bg-gray-200 px-4 py-2", children: [_jsxs("div", { className: "flex items-center w-[19.5%]", children: [_jsx("span", { className: "text-sm mr-2", children: "Go to" }), _jsx("input", { className: "border border-gray-300 rounded p-2 w-16", onChange: handleGotoChange, placeholder: "00:00", type: "text", value: gotoTime }), _jsx("button", { className: "text-sm px-4 py-2 rounded ml-0", onClick: handleGotoClick, children: "Show" })] }), _jsxs("div", { className: "flex items-center w-[71.5%]", children: [isPlaying ? (_jsx(CatIconPause, { className: "mr-2", "data-test": "pause-button", onClick: handlePauseClick })) : (_jsx(CatIconPlayArrow, { className: "mr-2", "data-test": "play-button", onClick: handlePlayClick })), _jsx("div", { className: "mr-2", children: "00:00" }), _jsx("input", { className: "w-[100%] h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer range-sm dark:bg-gray-700", "data-test": "time-slider", id: "timeSlider", max: "86400", min: "0", onChange: (event) => updateRangeValue(event.target.value), step: "1", type: "range", value: sliderValue }), _jsx("div", { className: "ml-2", children: "23:59" })] })] })), _jsxs("table", { className: "table-auto w-full text-left bg-white rounded-lg", "data-test": "assets-portfoilo-table", children: [_jsx("thead", { className: "sticky top-0 border-b py-4 z-10 bg-white", children: _jsxs("tr", { children: [_jsx("th", { className: "px-4 text-sm w-[22%]", "data-test": "Assets-header", children: "Assets" }), _jsx("th", { className: "px-4 text-sm", children: "SoC" }), !appConfig?.release_1_1?.showReplaySlider ? (_jsx(_Fragment, { children: _jsxs("th", { className: "relative w-[56%]", colSpan: 6, children: [_jsx("input", { className: "w-full h-1 mb-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-sm dark:bg-gray-700", id: "timeSlider", max: "86400", min: "0", onChange: (event) => updateRangeValue(event.target.value), step: "1", type: "range", value: rangeValue }), _jsxs("div", { className: "flex justify-between text-sm", children: [_jsx("span", { children: "00:00" }), _jsx("span", { children: "04:00" }), _jsx("span", { children: "08:00" }), _jsx("span", { children: "12:00" }), _jsx("span", { children: "16:00" }), _jsx("span", { children: "20:00" }), _jsx("span", { children: "24:00" })] }), replayTimeline && (_jsx("div", { className: "flex mb-6", children: _jsx("div", { className: "absolute text-sm", id: "timeDisplay", style: {
                                                                    right: `${sliderPostion}px` // Adjust position dynamically
                                                                }, children: formatTime(rangeValue) }) }))] }) })) : (_jsxs(_Fragment, { children: [_jsx("th", { className: "w-[10%] text-sm", "data-test": "Time-00:00", children: "00:00" }), _jsx("th", { className: "w-[10%] text-sm", "data-test": "Time-04:00", children: "04:00" }), _jsx("th", { className: "w-[10%] text-sm", "data-test": "Time-08:00", children: "08:00" }), _jsx("th", { className: "w-[10%] text-sm", "data-test": "Time-12:00", children: "12:00" }), _jsx("th", { className: "w-[10%] text-sm", "data-test": "Time-16:00", children: "16:00" }), _jsxs("th", { className: "w-[12%] text-sm", children: [_jsx("span", { className: "float-left", "data-test": "Time-20:00", children: "20:00" }), _jsx("span", { className: "float-right", children: "00:00" })] })] })), _jsxs("th", { className: "w-[15%] text-sm pl-4 pr-2", "data-test": "Total-Value-Header", children: ["Total Charge ", _jsx("br", {}), "Total Discharge"] })] }) }), _jsxs("tbody", { "data-test": "Timeline-Body", children: [populateTimeLineDetails(assetsDetails), _jsx("img", { className: "absolute h-[91.5%] bottom-0 z-10 hidden", "data-test": "Timeline-Vertical-Line-Image", id: "timeline-chart-line", src: TimelineVerticalLine })] })] })] })) })) }) }));
};
export default ActivityTimeline;
